/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-tagless-components, getter-return, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import StaffPageAccess from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import { observes } from '@ember-decorators/object';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class PersonForm extends Component {
  @action
  setAuditReason(value) {
    this.auditReason = value;
  }

  @action
  setSelectedTab(value) {
    this.selectedTab = value;
  }

  @service
  session;

  @service
  environment;

  @service
  options;

  @service
  permissions;

  @service
  roles;

  @service
  toaster;

  PersonType = PersonType;
  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  selectedTab = 0;
  prefix = '';
  buttonStyle = 'fab';
  buttonWormholeTarget = null;

  // auditReason contains the state for the audit reason.
  @tracked
  auditReason = '';

  // showReason provides the open/closed state of the reason capture dialog.
  @tracked
  showReason = false;
  // isSaving guards against double-clicking Save, which would fire person.save()
  // twice concurrently and could corrupt the record.
  @tracked
  isSaving = false;

  // showErrors provides the open/closed state of the validation error dialog.
  @tracked
  showErrors = false;

  // person provides the Person whose fields are being shown/edited.
  person = null;

  // personType determines the type of Person being edited.
  @alias('person.type')
  personType;

  // Handle validation of email address field if we have a known conflict
  // which we want to prevent the user from specifying.
  emailFields = Object.freeze([
    'core:emailAddress',
    'core:studentEmailAddress',
  ]);

  @tracked conflictedEmailAddress = '';

  get emailValidations() {
    let validators = [
      {
        message: 'Please enter a valid email address',
        validate: (val) => {
          // Consider empty "valid", as the field should be required.
          // This means that we only show one validation error at a time.
          return val === '' || emailPattern.test(val);
        },
      },
    ];
    if (!isEmpty(this.conflictedEmailAddress)) {
      validators.push({
        message: 'This email address is already in use',
        validate: (val) => {
          return (
            val.toLowerCase().trim() !==
            this.conflictedEmailAddress.toLowerCase().trim()
          );
        },
      });
    }
    return validators;
  }

  @tracked emailRequired = false;

  @computed(
    'person.{type,fields.core:emailAddress.value,fields.core:archivedEmailAddress.value}',
    'emailRequired',
  )
  get showArchivedEmail() {
    if (this.get('person.type') !== PersonType.STAFF || this.emailRequired) {
      // Don't show "archived" email addresses for non-staff users, or if the
      // email address is required (which implies current employment).
      return false;
    }

    // Only show "archived" email address if there isn't a valid current one.
    let currentEmail = this.get('person.fields.core:emailAddress.value');
    return (
      (isEmpty(currentEmail) || !emailPattern.test(currentEmail)) &&
      !isEmpty(this.get('person.fields.core:archivedEmailAddress.value'))
    );
  }

  @optionAlias('core:staffPermissions:editStudents')
  canEditStudents;

  @computed('canEditStudents', 'person.type', 'session.scopes.[]')
  get readOnly() {
    if (this.permissions.meet('urn:linced:organisation:role:admin')) {
      // Admin users can always edit.
      return false;
    }

    if (this.canEditStudents) {
      // If editing is enabled for non-admin staff...
      switch (this.person.type) {
        case PersonType.LEARNER:
        case PersonType.CAREGIVER:
          // .. and the person is a Learner or Caregiver, allow editing.
          return false;
      }
    }

    // Don't allow editing.
    return true;
  }

  @computed('readOnly', 'session.school.jurisdiction')
  get readOnlySiblings() {
    if (this.readOnly) {
      // If the whole form is read-only, siblings are too.
      return true;
    }

    // Check if we're in a jurisdiction which doesn't allow changes.
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.AU_NSW:
        return true;
    }

    // Otherwise, assume that changes are allowed.
    return false;
  }

  @alias('roles.canManageStaffPermissions')
  canManageStaffPermissions;

  @alias('person.hasDirtyAttributes')
  recordIsDirty;

  @computed('fieldGroups.[]', 'personType')
  get groups() {
    return this.fieldGroups
      .filter((group) => {
        if (group.personType !== this.personType) {
          // Wrong person type - skip.
          return false;
        }

        switch (this.personType) {
          case PersonType.LEARNER:
            // Skip medical condition group for learners.
            return group.key !== 'medicalCondition';

          case PersonType.CAREGIVER:
            // Skip relationship group for caregivers.
            return group.key !== 'relationship';
        }

        // Include all other groups.
        return true;
      })
      .sort((a, b) =>
        a.sequence > b.sequence ? 1 : a.sequence < b.sequence ? -1 : 0,
      );
  }

  @computed('groups.[]', 'session.{scopes.[],roles.[]}')
  get visibleFieldsByGroup() {
    return this.groups.reduce((map, group) => {
      let fields = resolved(group.fields)
        .slice()
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
      if (!this.permissions.meet(Scopes.LINCED_MANAGER)) {
        // School staff can only see fields which they have the role for.
        fields = fields.filter((field) => {
          if (isEmpty(field.requiredRole)) {
            // No role required; include this field.
            return true;
          }
          return this.roles.has(field.requiredRole);
        });
      }
      map[group.id] = fields;
      return map;
    }, {});
  }

  @observes('person.id')
  _resetSelectedTab() {
    this.auditReason = '';
    this.selectedTab = 0;
    this.showErrors = false;
    this.showErrors = false;
    this.showReason = false;
    this.rerender();
  }

  @computed('person.jurisdiction', 'groups.[]')
  get genderField() {
    let key = 'core:genderLegal';
    switch (this.get('person.jurisdiction')) {
      case Jurisdiction.NZ_MOE:
        key = 'nzMOE:gender';
        break;
    }
    let field;
    for (let index = 0; index < this.groups.length; index++) {
      let group = this.groups[index];
      let groupFields = resolved(group.fields);
      if (isEmpty(groupFields)) {
        continue;
      }

      field = groupFields.find((f) => f.key === key);
      if (!isEmpty(field)) {
        break;
      }
    }
    return field;
  }

  @tracked isEdit = false;

  @computed('isEdit', 'person.type')
  get isEditStaff() {
    if (this.isEdit && this.person.get('type') === PersonType.STAFF) {
      return true;
    }
  }

  // shouldAudit checks to see if an audit is required based on the fields that
  // were changed.
  shouldAudit() {
    // hax for MoE testing as it's naggy af otherwise...
    // refer: Volkswagen emissions scandal
    if (this.environment.name !== 'staging') {
      return false;
    }

    switch (this.person.type) {
      case PersonType.STAFF:
      case PersonType.CAREGIVER:
      case PersonType.LINC_ED:
        // we don't need to audit for these types...
        return false;
    }

    // Only changes to the FTE and Type fields are logged by the backend.
    // If any other fields should trigger the audit, they'll need to be added
    // both here and in the backend in order for the logs to be saved.
    let fteChanged = this.person.get('fields.nzMOE:fte.hasDirtyAttributes');
    let typeChanged = this.person.get('fields.nzMOE:type.hasDirtyAttributes');
    return (fteChanged || typeChanged) && isEmpty(this.auditReason);
  }

  savePerson() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    let person = this.person;
    if (!isEmpty(this.auditReason)) {
      set(person, 'auditReason', this.auditReason);
    }
    let emailField = 'core:emailAddress';
    if (this.person.type === PersonType.LEARNER) {
      emailField = 'core:studentEmailAddress';
    }
    let emailAddress = person.get(`fields.${emailField}.value`);

    // For new users who haven't selected any roles,
    // Default them to restricted
    if (person.isNew && !person.roles.length) {
      person.setPageAccessLevel(StaffPageAccess.RESTRICTED);
    }

    person
      .save()
      .then(() => {
        // Trigger any requested additional action
        let onSave = this.onSave;
        if (!isEmpty(onSave)) {
          onSave(person);
        }
      })
      .then(() => {
        this.auditReason = '';
      })
      .catch((err) => {
        if (err?.errors?.[0]?.status === '409') {
          // Conflict - email address in use.
          // We shouldn't hit a 409 on jurisdictional ID conflict as we check for
          // and handle that separately (via the jurisdictional ID field).
          this.conflictedEmailAddress = emailAddress;
          this.toaster.error('Save failed - email address already in use.');
        }
        let onError = this.onError;
        if (!isEmpty(onError)) {
          onError(person, err);
        }
      })
      .finally(() => {
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.isSaving = false;
      });
  }

  @alias('person.pageAccessLevel')
  pageAccessLevel;

  @action
  saveUpdate(fromAudit) {
    if (this.isSaving) {
      return;
    }
    if (this.shouldAudit() && !fromAudit) {
      this.showReason = true;
    } else {
      this.savePerson();
      this.showReason = false;
    }
  }

  @action
  buttonClasses(isInvalid) {
    let classes = ['save'];
    if (this.buttonStyle === 'fab') {
      classes.push('md-fab-bottom-right');
    }
    if (isInvalid) {
      classes.push('invalid');
    }
    return classes.join(' ');
  }

  @action
  handleShowErrors() {
    this.showErrors = true;
  }

  @action
  hideErrors() {
    this.showErrors = false;
  }

  @action
  hideReason() {
    this.showReason = false;
    this.auditReason = '';
  }

  @action
  setPageAccessLevel(pageAccessLevel) {
    this.person.setPageAccessLevel(pageAccessLevel);
  }

  @action
  toggleRole(role, checked) {
    if (checked) {
      this.person.roles.pushObject(role);
    } else {
      this.person.roles.removeObject(role);
    }
  }
}
